import { apiRequest } from "../api";

export type TicketAccessMode = "open" | "restricted" | "hybrid";

export interface OrganizationSettingsResponse {
  ticketAccessMode: TicketAccessMode;
  allowClientSelfRegistration: boolean;
  clientPortalUrl?: string | null;
  slaAlertIntervalMinutes: number;
}

export type BusinessHoursWeekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export interface BusinessHoursPeriod {
  start: string;
  end: string;
}

export interface BusinessHoursDaySchedule {
  enabled: boolean;
  periods: BusinessHoursPeriod[];
}

export interface BusinessHoursSettings {
  enabled: boolean;
  timezone: string;
  schedule: Record<BusinessHoursWeekday, BusinessHoursDaySchedule>;
}

export interface OrganizationInfo {
  name: string;
  subdomain: string;
}

export const organizationSettingsService = {
  async getInfo(): Promise<[OrganizationInfo | null, { msg: string; code?: string } | null]> {
    return apiRequest<OrganizationInfo>({
      loaderKey: "orgInfo",
      method: "get",
      url: "/app/organization",
    });
  },

  async getSettings(): Promise<[OrganizationSettingsResponse | null, { msg: string; code?: string } | null]> {
    return apiRequest<OrganizationSettingsResponse>({
      loaderKey: "orgSettings",
      method: "get",
      url: "/app/organization/settings",
    });
  },

  async updateTicketAccessMode(
    ticketAccessMode: TicketAccessMode
  ): Promise<[OrganizationSettingsResponse | null, { msg: string; code?: string } | null]> {
    return apiRequest<OrganizationSettingsResponse>({
      loaderKey: "orgSettingsUpdate",
      method: "patch",
      url: "/app/organization/settings",
      data: { ticketAccessMode },
    });
  },

  async updateSlaAlertInterval(
    slaAlertIntervalMinutes: number
  ): Promise<[OrganizationSettingsResponse | null, { msg: string; code?: string } | null]> {
    return apiRequest<OrganizationSettingsResponse>({
      loaderKey: "orgSlaAlertSettingsUpdate",
      method: "patch",
      url: "/app/organization/sla-alert-settings",
      data: { slaAlertIntervalMinutes },
    });
  },

  async getGithubRepos(): Promise<[{ githubRepos: string[] } | null, { msg: string; code?: string } | null]> {
    return apiRequest<{ githubRepos: string[] }>({
      loaderKey: "orgGithubRepos",
      method: "get",
      url: "/app/organization/github-repos",
    });
  },

  async updateGithubRepos(
    githubRepos: string[]
  ): Promise<[{ githubRepos: string[] } | null, { msg: string; code?: string } | null]> {
    return apiRequest<{ githubRepos: string[] }>({
      loaderKey: "orgGithubReposUpdate",
      method: "patch",
      url: "/app/organization/github-repos",
      data: { githubRepos },
    });
  },

  async getBusinessHours(): Promise<[BusinessHoursSettings | null, { msg: string; code?: string } | null]> {
    return apiRequest<BusinessHoursSettings>({
      loaderKey: "orgBusinessHours",
      method: "get",
      url: "/app/organization/business-hours",
    });
  },

  async updateBusinessHours(
    payload: Partial<BusinessHoursSettings>
  ): Promise<[BusinessHoursSettings | null, { msg: string; code?: string } | null]> {
    return apiRequest<BusinessHoursSettings>({
      loaderKey: "orgBusinessHoursUpdate",
      method: "patch",
      url: "/app/organization/business-hours",
      data: payload,
    });
  },
};
